import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Undo2 } from "lucide-react";
import PageTitle from "../components/common/PageTitle";
import LoadingOverlay from "../components/common/LoadingOverlay";
import FilterDropdown from "../components/common/FilterDropdown";
import FormInput from "../components/forms/FormInput";
import { showError, showSuccess } from "../utils/toast";
import { validateFields } from "../utils/formValidation";
import { ROUTES, JWT } from "../config/constants";
import { API_BASE_URL } from "../api/apiClient";

export default function InovasiForm() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  
  // Form fields
  const [projectId, setProjectId] = useState("");
  const [judulMakalah, setJudulMakalah] = useState("");
  const [tanggalRencana, setTanggalRencana] = useState("");
  const [tanggalAktual, setTanggalAktual] = useState("");

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      // Endpoint disesuaikan dengan route baru yang mengambil semua project tanpa filter
      const res = await fetch(`${API_BASE_URL}/api/get-all-projects`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`
        }
      });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load projects", err);
      showError("Gagal memuat data project");
    }
  }

  function validateForm() {
    const rules = [
      { value: projectId, message: "Project wajib dipilih" },
      { value: judulMakalah, message: "Judul Makalah Inovasi wajib diisi" },
      { value: tanggalRencana, message: "Tanggal rencana selesai wajib diisi" },
    ];
    return validateFields(rules);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        project_id: projectId,
        judul_makalah: judulMakalah,
        tanggal_rencana_selesai: tanggalRencana,
        tanggal_aktual_selesai: tanggalAktual || null // null jika belum ada
      };

      const res = await fetch(`${API_BASE_URL}/api/monitoring-inovasi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Gagal menyimpan data");

      showSuccess("Progres Inovasi berhasil disimpan");
      navigate(ROUTES.LESSONS); // Ganti dengan route list inovasi jika ada
    } catch (err) {
      console.error(err);
      showError(err.message || "Terjadi kesalahan server");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container mx-auto px-4 py-6 pt-20">
      <LoadingOverlay isLoading={loading} />

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(ROUTES.LESSONS)}
          className="flex items-center text-sm gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <Undo2 size={14} />
          <span>Kembali</span>
        </button>

        <PageTitle
          title="Input Progres Makalah Inovasi"
          subtitle="Catat rencana dan aktualisasi pembuatan makalah inovasi proyek."
        />
      </div>

      {/* Form Section */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        {/* Dropdown Project (Semua Project tanpa filter) */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project <span className="text-red-500">*</span>
          </label>
          <FilterDropdown
            options={[
              { value: "", label: "Pilih Project" },
              ...projects.map((p) => ({
                value: p.id,
                label: p.name,
              })),
            ]}
            value={projectId}
            onChange={(value) => setProjectId(value)}
            placeholder="Pilih Project"
          />
        </div>

        {/* Input Judul Makalah */}
        <FormInput
          label="Judul Makalah Inovasi"
          value={judulMakalah}
          onChange={(val) => setJudulMakalah(val)}
          placeholder="Contoh: Optimalisasi Pengecoran..."
          maxLength={500}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Input Tanggal Rencana Selesai */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tanggal Rencana Selesai <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={tanggalRencana}
              onChange={(e) => setTanggalRencana(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              required
            />
          </div>

          {/* Input Tanggal Aktual Selesai */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tanggal Aktual Selesai
            </label>
            <input
              type="date"
              value={tanggalAktual}
              onChange={(e) => setTanggalAktual(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
            />
            <p className="text-xs text-gray-500 mt-1">Kosongkan jika inovasi belum selesai.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(ROUTES.LESSONS)}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Menyimpan..." : "Simpan Progres"}
          </button>
        </div>
      </form>
    </div>
  );
}
